import { EventEmitter, Injectable, Output, inject } from '@angular/core';
import { LightDarkThemes, Themes } from 'src/models/AppTheme';
import { SettingsService } from '../settings/settings.service';
import { isK3000domain } from 'src/app/core/common';
import { CurrentExamService } from '../exam/current-exam.service';
import { DOCUMENT } from '@angular/common';
import { ISettings } from 'src/app/core/interfaces';

export interface IAppThemeProperty {
  name: string;
  value: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeColorService {
  private changesCounter: number = 0;
  private currentLightDarkTheme: LightDarkThemes = LightDarkThemes.Light;
  private currentTheme = this.domainTheme;
  private documentWrapper = inject(DOCUMENT);

  get themeChangesCounter(): number { return this.changesCounter; }

  @Output() ThemeChanged: EventEmitter<LightDarkThemes> = new EventEmitter();

  private get domainTheme(): Themes {
    return isK3000domain() ? Themes.K3000 : Themes.Alinea;
  }

  constructor(
    public settingService: SettingsService,
    private currentExamService: CurrentExamService,
  ) {
    this.settingService.getSettingsObservable().subscribe(s => this.toggleLightDarkTheme(s));
    this.currentExamService.inProgressObservable.subscribe(e => this.toggleExamTheme(e));
    this.applyDomainThemeClass();
    this.changeTheme();
  }

  private changeTheme() {
    this.changesCounter++;
    const themeName = this.currentTheme.toString() + '-' + this.currentLightDarkTheme.toString();
    const themeLink = this.documentWrapper.getElementById('app-theme') as HTMLLinkElement;

    if (themeLink) {
      themeLink.href = themeName + '.css';
    }

    this.ThemeChanged.emit(this.currentLightDarkTheme);
  }

  setProperty(name: string, value: string): void {
    document.documentElement.style.setProperty(`--${name}`, value);
  }

  getCurrentLightDarkTheme(): LightDarkThemes {
    return this.currentLightDarkTheme;
  }

  private toggleExamTheme(activate: boolean) {
    if (activate && this.currentTheme !== Themes.ExamMode) {
      this.currentTheme = Themes.ExamMode;
      this.changeTheme();
      return;
    }

    if (!activate && this.currentTheme === Themes.ExamMode) {
      this.currentTheme = this.domainTheme;
      this.changeTheme();
      return;
    }
  }

  private toggleLightDarkTheme(settings: ISettings) {
    if (settings.darkMode && this.currentLightDarkTheme !== LightDarkThemes.Dark) {
      this.currentLightDarkTheme = LightDarkThemes.Dark;
      this.changeTheme();
      this.applyMaterialThemeClass(true);
      return;
    }

    if (!settings.darkMode && this.currentLightDarkTheme === LightDarkThemes.Dark) {
      this.currentLightDarkTheme = LightDarkThemes.Light;
      this.changeTheme();
      this.applyMaterialThemeClass(false);
      return;
    }
  }

  private applyMaterialThemeClass(isDarkMode: boolean | undefined): void {
    this.documentWrapper.body.classList.toggle('mat-dark-theme', !!isDarkMode);
  }

  private applyDomainThemeClass(): void {
    this.documentWrapper.body.classList.toggle('k3000-domain', isK3000domain());
  }
}
